import React from "react";
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import { Button, Input, Modal, Table } from 'antd';

import { BitmakerAPIClient } from "../../services/BitmakerApp";
import { Category, CategoryFromJSON, CompanyData, CompanyDataFromJSON } from "../../bitmaker/schemas/Leads";

const { Column } = Table;


interface Props {
}

interface State {
    selectedCategory: string | null;
    companiesData: Array<CompanyData>;
    validatedEmailsCount: number;
    requestedEmailsCount: number | null;
    showModal: boolean;
    companyCategories: Array<Category>;
    showError: boolean;
}

export class Leads extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            selectedCategory: null,
            companiesData: [],
            validatedEmailsCount: 0,
            requestedEmailsCount: 0,
            showModal: false,
            companyCategories: [],
            showError: false
        };
    }

    componentDidMount() {
        try {
            BitmakerAPIClient.get(
                `/business_intelligence/company_categories`,
                null,
                (response) => {
                    this.setState({
                        companyCategories: (response.data as Array<Category>).map(CategoryFromJSON)
                    });
                }
            );
        } catch (error) {
            console.error('Something went wrong when calling company_categories endpoint: ', error);
        }
    }

    handleCheckboxChange = (selectedCategory: string) => {
        this.setState({ selectedCategory });
    }

    handleExportCSV = () => {
        const { selectedCategory } = this.state;
        const category = this.state.companyCategories.find((cat) => cat.name === selectedCategory);

        if (category) {
            const validatedEmailsCount = category.validated_emails_count;
            if (validatedEmailsCount) {
                this.setState({
                    validatedEmailsCount,
                    showModal: true,
                    showError: false
                });
            } else {
                this.setState({ showError: true });
            }
        }
    }

    handleRequestedEmailsCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.target.value;
        const requestedEmailsCount = input === "" ? null : parseInt(input);
        this.setState({ requestedEmailsCount });
    }

    handleDownloadCSV = () => {
        const { requestedEmailsCount, selectedCategory } = this.state;
        try {
            BitmakerAPIClient.get(
                `/business_intelligence/companies_data`,
                { requestedEmailsCount: requestedEmailsCount, category: selectedCategory },
                (response) => {
                    this.setState(
                        {
                            companiesData: (response.data as Array<CompanyData>).map(CompanyDataFromJSON),
                            showModal: false,
                            validatedEmailsCount: 0,
                            requestedEmailsCount: 0,
                            selectedCategory: null
                        },
                        () => {
                            this.generateAndDownloadCSV();
                        }
                    );
                }
            );
        } catch (error) {
            console.error('Something went wrong when calling companies_data endpoint: ', error);
        }
    }

    generateAndDownloadCSV() {
        const { companiesData } = this.state;
        const columnTitles = ['Name', 'ICP', 'LinkedIn', 'Website', 'Categories', 'Email'];

        const csvData = []
        companiesData.forEach((company) => {
            company.emails.forEach((email) => {
                const rowData = [
                    company.name,
                    company.icp,
                    company.linkedin,
                    company.website,
                    company.categories.join(", "),
                    email
                ];
                csvData.push(rowData);
            });
        });

        // Insert column titles at the beginning of the data array
        csvData.unshift(columnTitles);

        // Generate CSV
        const csv = Papa.unparse(csvData);

        // Create the blob and download the file
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, 'companies_data.csv');
    }

    public render() {
        const { selectedCategory, requestedEmailsCount, showModal, validatedEmailsCount } = this.state;
        const requestedEmailsCountIsValid = requestedEmailsCount !== null && requestedEmailsCount > 0 && requestedEmailsCount <= validatedEmailsCount;
        const requestedEmailsCountError = requestedEmailsCountIsValid ? "" : "Please enter a valid email count";

        const categoryColumns = this.state.companyCategories.map((category) => ({
            key: category.name,
            category: category.name,
            quantity: category.validated_emails_count,
        }));
        const totalQuantity = categoryColumns.reduce((total, item) => total + (item.quantity || 0), 0);

        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Table
                    style={{ width: '50%', marginTop: '40px' }}
                    size={'small'}
                    dataSource={categoryColumns}
                    pagination={false}
                    rowSelection={{
                        type: 'radio',
                        selectedRowKeys: selectedCategory ? [selectedCategory] : [],
                        onChange: (selectedCategory) => {
                            if (selectedCategory.length > 0) {
                                this.handleCheckboxChange(selectedCategory[0] as string);
                            }
                        },
                    }}
                >
                    <Column title="Category" dataIndex="category" key="category" />
                    <Column title="Valid Emails" dataIndex="quantity" key="quantity" />
                </Table>

                <div style={{ marginTop: '20px', fontSize: '0.9em' }}>
                    Total Valid Emails: {totalQuantity}
                </div>

                {this.state.showError && (
                    <div style={{ color: 'red', fontSize: '0.8em', marginTop: '20px' }}>
                        No emails available for the selected category.
                    </div>
                )}

                <Button type="primary" onClick={this.handleExportCSV} style={{ margin: '20px 0' }}>
                    Export to CSV
                </Button>

                <Modal
                    title="Download Emails"
                    open={showModal}
                    onOk={this.handleDownloadCSV}
                    onCancel={() => this.setState({ showModal: false, validatedEmailsCount: 0, requestedEmailsCount: 0, selectedCategory: null })}
                    okButtonProps={{ disabled: !requestedEmailsCountIsValid }}
                >
                    <p>Total Emails Recovered: {validatedEmailsCount}</p>
                    <Input
                        type="number"
                        placeholder="Enter email count"
                        value={requestedEmailsCount?.toString() ?? ''}
                        onChange={this.handleRequestedEmailsCountChange}
                        style={{ marginBottom: '10px' }}
                    />
                    {requestedEmailsCountError && <p style={{ color: 'red' }}>{requestedEmailsCountError}</p>}
                    {requestedEmailsCountIsValid && <p>Are you sure you want to download {requestedEmailsCount ? requestedEmailsCount : 0} emails?</p>}
                </Modal>
            </div>
        );
    }
}
